import React, {useState, useMemo, useEffect} from 'react';
import {useFormData, useJobs, useJobsContent} from "../../api/api";
import { categorizeItems } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Error from "../404/Error";
import Container from "../../components/Container";
import JobCard from "../../components/JobCard";
import InnerPage from "../../components/InnerPage";
import BG from './assets/bg_jobs.webp';
import Preloader from "../../components/Preloader";
import Button from "../../components/Button";
import skull from "../../components/Form/assets/skull.png";
import Form from "../../components/Form";
import Thanks from "../../components/Form/Thanks";
import Page from "../../components/Page";
import styles from './jobs.module.scss';

const Jobs = () => {
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const { jobsData, isJobsLoading, jobsError } = useJobs(lang);
  const { jobsContentData, isJobsContentLoading, jobsContentError } = useJobsContent(lang);

  const [errorRequest, setErrorRequest] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [thanksImg, setThanksImg] = useState(skull);

  const { formFields, isFormFieldsLoading, formFieldsError} = useFormData(lang);

  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = useMemo(() => jobsData ? categorizeItems(jobsData.data) : [], [jobsData]);
  const filteredJobs = useMemo(() => {
    if (!jobsData || selectedCategory === 'All') {
      return jobsData?.data;
    }
    return jobsData.data.filter(item => item.category === selectedCategory);
  }, [jobsData, selectedCategory]);

  useEffect( () => {
    setThanksImg(skull);
  }, []);

  const onApply = () => {
    setShowForm(true);
  }

  if (isJobsContentLoading || isJobsLoading || isFormFieldsLoading) {
    return <Preloader />
  }

  if (jobsContentError || jobsError || formFieldsError || formFields.error) {
    return <Error />;
  }

  const formData = formFields.data;

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio | {t("pages.jobs")}</title>
      </Helmet>
      <InnerPage className={styles.wrapper} background={BG}>
        <Container className={`${styles.page}`}>
          { !showForm && !showThanks &&
            <>
              {jobsContentData &&
                <div className="row">
                  <div className="col-lg-12 text-center">
                    <h2 className='text-pink'>{jobsContentData?.data?.header?.title ?? ''}</h2>
                  </div>
                </div>
              }
              <div className="row">
                <div className="col-lg-12 text-center">
                  { jobsData.data.length >= 6 &&
                    <div className={styles.categories}>
                      <span className={styles.category} onClick={() => setSelectedCategory('All')}>
                        All ({jobsData.data.length})
                      </span>
                      {categories.map(({category, length}) => (
                        <span
                          className={styles.category}
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                        >
                          {category} ({length})
                        </span>
                      ))}
                    </div>
                  }
                </div>
              </div>
              <div className="row">
                {
                  filteredJobs && filteredJobs.length > 0 ? (
                    <div className={styles.jobs}>
                      {filteredJobs.map((job) => (
                        <JobCard key={job.id} data={job} />
                      ))}
                    </div>
                  ) : (<></>)
                }
              </div>
              {jobsContentData &&
                <div className="col-lg-12 text-center">
                  <div className="text-large">
                    <p>{jobsContentData?.data?.header?.description ?? ''}</p>
                  </div>
                  <div style={{display: "flex", justifyContent: "center", marginTop: "50px"}}>
                    <Button text={t("buttons.apply")} view={'light'} onClick={onApply}/>
                  </div>
                </div>
              }
            </>
          }
          { showForm && !showThanks &&
            <div className="row flex-col-center-v">
              <div className="col-sm-10 col-md-8 col-xs-12">
                <Form
                  title={''}
                  setShowForm={setShowForm}
                  setShowThanks={setShowThanks}
                  setErrorRequest={setErrorRequest}
                  sendTo={formData.recruitment}
                />
              </div>
            </div>
          }

          <div className="row flex-col-center-v" style={{display: `${!(!showForm && showThanks && !isFormFieldsLoading) ? 'none' : 'flex'}`}}>
            <div className="col-lg-8">
              <Thanks
                title={t("form.thanks")}
                img={thanksImg}
                text={formData.thanks}
                setShowForm={setShowForm}
                setShowThanks={setShowThanks}
                errorRequest={errorRequest}
                errorTitle={t("form_errors.error_title")}
                errorText={t("form_errors.error_text")}
              />
            </div>
          </div>
        </Container>
      </InnerPage>
    </Page>
  );
};

export default Jobs;